import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Button,
  TextField,
} from "@mui/material";
import { Close, Add, Remove } from "@mui/icons-material";
import { formatPrice, stripHtml } from "../utils/helpers";

export default function ProductOptionsSheet({ product, currency, onClose, onConfirm }) {
  const options = product?.product_options || [];
  const addons = product?.addons || [];
  const description = stripHtml(product?.description);

  const [optionId, setOptionId] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState({}); // { [addonId]: quantity }
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!product) return;
    const defaultOption = options.find((o) => o.selected_default) || options[0] || null;
    setOptionId(defaultOption ? defaultOption.id : null);
    const defaultAddons = {};
    addons.forEach((a) => {
      if (a.pivot?.selected_default) defaultAddons[a.id] = 1;
    });
    setSelectedAddons(defaultAddons);
    setQuantity(1);
    setNotes("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  if (!product) return null;

  const toggleAddon = (addon) => {
    setSelectedAddons((prev) => {
      const next = { ...prev };
      if (next[addon.id]) delete next[addon.id];
      else next[addon.id] = 1;
      return next;
    });
  };

  const selectedOption = options.find((o) => o.id === optionId) || null;
  const unitPrice =
    (selectedOption ? Number(selectedOption.price) : Number(product.offer_price ?? product.price ?? 0)) +
    Object.entries(selectedAddons).reduce((sum, [id, qty]) => {
      const addon = addons.find((a) => a.id === Number(id));
      return sum + (addon ? Number(addon.pivot?.price ?? addon.price) * qty : 0);
    }, 0);

  const handleAdd = () => {
    const chosenAddons = Object.entries(selectedAddons).map(([id, qty]) => {
      const addon = addons.find((a) => a.id === Number(id));
      return { id: addon.id, name: addon.name, price: addon.pivot?.price ?? addon.price, quantity: qty };
    });
    onConfirm({
      product,
      option: selectedOption ? { id: selectedOption.id, name: selectedOption.name, price: selectedOption.price } : null,
      addons: chosenAddons,
      quantity,
      notes,
    });
  };

  return (
    <Drawer anchor="bottom" open={!!product} onClose={onClose} PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "85dvh" } }}>
      <Box sx={{ p: 2, pb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {product.name}
            </Typography>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        {options.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Choose an option
            </Typography>
            <RadioGroup value={optionId} onChange={(e) => setOptionId(Number(e.target.value))}>
              {options.map((o) => (
                <FormControlLabel
                  key={o.id}
                  value={o.id}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", minWidth: 220 }}>
                      <span>{o.name}</span>
                      <Typography variant="body2" color="text.secondary">
                        {formatPrice(o.price, currency)}
                      </Typography>
                    </Box>
                  }
                  sx={{ width: "100%", mr: 0 }}
                />
              ))}
            </RadioGroup>
          </>
        )}

        {addons.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Add-ons
            </Typography>
            {addons.map((a) => (
              <FormControlLabel
                key={a.id}
                control={<Checkbox size="small" checked={!!selectedAddons[a.id]} onChange={() => toggleAddon(a)} />}
                label={
                  <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", minWidth: 220 }}>
                    <span>{a.name}</span>
                    <Typography variant="body2" color="text.secondary">
                      +{formatPrice(a.pivot?.price ?? a.price, currency)}
                    </Typography>
                  </Box>
                }
                sx={{ width: "100%", mr: 0 }}
              />
            ))}
          </>
        )}

        <Divider sx={{ my: 1.5 }} />
        <TextField
          label="Notes for kitchen (optional)"
          fullWidth
          size="small"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          minRows={1}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <IconButton size="small" onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
              <Remove fontSize="small" />
            </IconButton>
            <Typography sx={{ px: 1.5, minWidth: 24, textAlign: "center" }}>{quantity}</Typography>
            <IconButton size="small" onClick={() => setQuantity((q) => q + 1)}>
              <Add fontSize="small" />
            </IconButton>
          </Box>
          <Button variant="contained" size="large" onClick={handleAdd} sx={{ px: 4 }}>
            Add · {formatPrice(unitPrice * quantity, currency)}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
